import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "wax_token";

export const api = axios.create({ baseURL: API });

// Attach token to every request
api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 globally — clear token and reload (auth provider will catch)
api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            const onLogin = window.location.pathname === "/login";
            if (!onLogin) {
                localStorage.removeItem(TOKEN_KEY);
                // Trigger auth re-check via custom event
                window.dispatchEvent(new Event("auth:logout"));
            }
        }
        return Promise.reject(err);
    }
);

export const CONDITION_RANK = {
    "Poor": 1, "Good": 2, "Excellent": 3, "Near Mint": 4, "Mint": 5
};

export const CONDITION_COLORS = {
    "Poor": "bg-destructive/10 text-destructive border-destructive/30",
    "Good": "bg-muted text-muted-foreground border-muted-foreground/20",
    "Excellent": "bg-accent/10 text-accent border-accent/30",
    "Near Mint": "bg-primary/10 text-primary border-primary/30",
    "Mint": "bg-primary text-primary-foreground border-primary",
};

export const LOCATION_COLORS = {
    "Ruth": "bg-[#C05A35]/10 text-[#C05A35] border-[#C05A35]/30",
    "Andy": "bg-[#4A5D4E]/10 text-[#4A5D4E] border-[#4A5D4E]/30",
    "Archive": "bg-foreground/10 text-foreground/70 border-foreground/20",
    "Lagom": "bg-[#5A7D8C]/10 text-[#5A7D8C] border-[#5A7D8C]/30",
};

export const PRIORITY_COLORS = {
    "Must have": "bg-primary/10 text-primary border-primary/30",
    "Nice to have": "bg-muted text-muted-foreground border-muted-foreground/20",
};

export const PLACEHOLDER_ARTWORK = "https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHZpbnlsJTIwcmVjb3JkJTIwc2xlZXZlJTIwYXJ0fGVufDB8fHx8MTc3OTk1OTg5MXww&ixlib=rb-4.1.0&q=85";

export const COLOURS = ["Black", "Blue", "Clear", "Gold", "Green", "Grey", "Marble", "Orange", "Pink", "Purple", "Red", "Smoked", "Yellow"];
export const AVAILABILITIES = ["Common", "Uncommon", "Limited", "Rare"];

export const AVAILABILITY_COLORS = {
    "Common": "bg-muted text-muted-foreground border-muted-foreground/20",
    "Uncommon": "bg-accent/10 text-accent border-accent/30",
    "Limited": "bg-primary/10 text-primary border-primary/30",
    "Rare": "bg-primary text-primary-foreground border-primary",
};

export const COLOUR_SWATCHES = {
    "Black": "#111111",
    "Blue": "#3B6EA5",
    "Clear": "#E8E8E8",
    "Gold": "linear-gradient(135deg, #E8C061 0%, #B8892E 50%, #E8C061 100%)",
    "Green": "#4A7A4A",
    "Grey": "#8A8A8A",
    "Marble": "linear-gradient(135deg, #E8E4DA 0%, #6E6A62 50%, #E8E4DA 100%)",
    "Orange": "#D97742",
    "Pink": "#E8A2C0",
    "Purple": "#6E4E8A",
    "Red": "#C0392B",
    "Smoked": "linear-gradient(135deg, #6E6A62 0%, #2E2A26 60%, #4E4A44 100%)",
    "Yellow": "#E6C24B",
};

/**
 * Format an ISO/YYYY-MM-DD/timestamp string as DD/MM/YYYY.
 * Returns empty string on falsy or unparseable input.
 */
export function formatDate(value) {
    if (!value) return "";
    // Handle plain YYYY-MM-DD without timezone shift
    const ymd = typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

/** Format an amount as £X.XX (2dp). Returns empty string when null/undefined. */
export function formatMoney(value) {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (isNaN(n)) return "";
    return `£${n.toFixed(2)}`;
}

/** Format an amount as whole £X with no decimals, thousands-separated. */
export function formatMoneyWhole(value) {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (isNaN(n)) return "";
    return `£${Math.round(n).toLocaleString("en-GB")}`;
}

export const EMPTY_STATE_IMG = "https://images.pexels.com/photos/9258252/pexels-photo-9258252.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const HERO_IMG = "https://images.unsplash.com/photo-1766592946837-ab4454c4a8a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHx2aW55bCUyMHJlY29yZCUyMHBsYXllciUyMHZpbnRhZ2UlMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzc5OTU5ODkxfDA&ixlib=rb-4.1.0&q=85";

export function resolveArtworkUrl(record) {
    if (!record) return PLACEHOLDER_ARTWORK;
    const url = record.artwork_url;
    if (!url) return PLACEHOLDER_ARTWORK;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) {
        const token = localStorage.getItem(TOKEN_KEY);
        const sep = url.includes("?") ? "&" : "?";
        return `${BACKEND_URL}${url}${token ? `${sep}auth=${encodeURIComponent(token)}` : ""}`;
    }
    return url;
}
